//frontend/src/site/templates/TemplateCorretorLovable/pages/Properties.jsx
import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Search,
  SlidersHorizontal,
  X,
  ArrowLeft,
  Phone,
  Grid3X3,
  List,
} from 'lucide-react';

// ✅ UI isolada do Lovable
import { Button } from '@/site/templates/TemplateCorretorLovable/ui/button';
import { Input } from '@/site/templates/TemplateCorretorLovable/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/site/templates/TemplateCorretorLovable/ui/select';

/* ======================
   HELPERS
====================== */

const onlyDigits = (v = '') => String(v).replace(/\D/g, '');

const formatBRL = (value) => {
  const n = Number(value || 0);
  if (Number.isNaN(n)) return 'R$ 0';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// ✅ ATUALIZADO: Converte item de imagem (string ou objeto) -> url string
// Agora suporta fileUrl e link, comuns no seu banco de dados
function getImageUrl(img) {
  if (!img) return null;
  if (typeof img === 'string') return img;
  if (typeof img === 'object') {
    return (
      img.url || img.src || img.downloadURL || img.fileUrl || img.link || null
    );
  }
  return null;
}

// ✅ ATUALIZADO: Pega 1ª imagem válida varrendo múltiplos campos do Elion/Zimmo
function pickMainImage(p) {
  // 1. Tenta campos de imagem única ou destaque (incluindo fotoDestaque e capa)
  const fromThumb =
    getImageUrl(p.thumbnail) ||
    getImageUrl(p.capaUrl) ||
    getImageUrl(p.fotoDestaque) ||
    getImageUrl(p.capa);

  if (fromThumb) return fromThumb;

  // 2. Varre os arrays (priorizando 'fotos' que é o padrão do seu sistema)
  const arr = Array.isArray(p.fotos)
    ? p.fotos
    : Array.isArray(p.imagens)
      ? p.imagens
      : Array.isArray(p.images)
        ? p.images
        : [];

  for (const it of arr) {
    const u = getImageUrl(it);
    if (u) return u;
  }
  return null;
}

// Normaliza o shape do imóvel vindo do Elion/Algolia/Firestore
function normalizeProperty(p = {}) {
  const id = p.id || p.objectID || p.uid || p.refCodigo || p.ref || '';

  const statusRaw = String(p.status || p.negocioTipo || p.finalidade || '')
    .toLowerCase()
    .trim();

  const status =
    statusRaw.includes('loc') || statusRaw.includes('alug')
      ? 'Locação'
      : 'Venda';

  const price = Number(p.preco ?? p.price ?? 0);
  const title = p.titulo || p.title || p.tipo || p.type || p.nome || 'Imóvel';
  const neighborhood = p.bairro || p.neighborhood || '';
  const city = p.cidade || p.city || '';

  const location =
    p.location ||
    [neighborhood, city].filter(Boolean).join(', ') ||
    city ||
    '—';

  const beds = Number(p.dormitorios ?? p.beds ?? 0);
  const baths = Number(p.banheiros ?? p.baths ?? 0);

  const area =
    Number(p.areaConstruida ?? p.area ?? p.m2 ?? 0) ||
    Number(p.areaTerreno ?? 0);

  const type = p.tipo || p.type || 'Imóvel';
  const featured = Boolean(p.destaque || p.featured);

  // ✅ Usa a lógica aprimorada de imagem
  const image = pickMainImage(p);

  return {
    id: String(id || ''),
    image,
    title,
    location,
    neighborhood: neighborhood || '—',
    city: city || '—',
    price,
    priceFormatted: formatBRL(price),
    beds,
    baths,
    area,
    type,
    status,
    featured,
    raw: p,
  };
}

function getBasePath({ handle, slug, pathname }) {
  const path = String(pathname || '').toLowerCase();
  if (path.startsWith('/u/')) {
    const h = String(handle || slug || '').trim();
    return h ? `/u/${h}` : '/u';
  }
  const s = String(slug || handle || '').trim();
  return s ? `/site/${s}` : '/site';
}

const Properties = ({ siteConfig, properties = [], slug, handle }) => {
  const { pathname } = useLocation();

  const basePath = useMemo(
    () => getBasePath({ handle, slug, pathname }),
    [handle, slug, pathname]
  );

  const brandName = siteConfig?.displayName || siteConfig?.nome || 'Corretor';
  const whatsapp =
    siteConfig?.whatsapp ||
    siteConfig?.whatsappDisplay ||
    siteConfig?.telefone ||
    '';
  const waLink = whatsapp ? `https://wa.me/55${onlyDigits(whatsapp)}` : null;

  const allProperties = useMemo(
    () => (Array.isArray(properties) ? properties : []).map(normalizeProperty),
    [properties]
  );

  // Filtros
  const propertyTypes = useMemo(() => {
    const set = new Set(allProperties.map((p) => p.type).filter(Boolean));
    return ['Todos', ...Array.from(set)];
  }, [allProperties]);

  const statusTypes = ['Todos', 'Venda', 'Locação'];

  const neighborhoods = useMemo(() => {
    const set = new Set(
      allProperties.map((p) => p.neighborhood).filter(Boolean)
    );
    return ['Todos', ...Array.from(set)];
  }, [allProperties]);

  const bedroomOptions = ['Todos', '1+', '2+', '3+', '4+', '5+'];

  const priceRanges = [
    { label: 'Qualquer valor', min: 0, max: Infinity },
    { label: 'Até R$ 1 milhão', min: 0, max: 1000000 },
    { label: 'R$ 1 - 3 milhões', min: 1000000, max: 3000000 },
    { label: 'R$ 3 - 5 milhões', min: 3000000, max: 5000000 },
    { label: 'Acima de R$ 5 milhões', min: 5000000, max: Infinity },
  ];

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('Todos');
  const [selectedStatus, setSelectedStatus] = useState('Todos');
  const [selectedBedrooms, setSelectedBedrooms] = useState('Todos');
  const [selectedPriceRange, setSelectedPriceRange] = useState(0);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('Todos');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  const filteredProperties = useMemo(() => {
    return allProperties.filter((property) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          property.title.toLowerCase().includes(query) ||
          property.location.toLowerCase().includes(query) ||
          property.type.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (selectedType !== 'Todos' && property.type !== selectedType)
        return false;
      if (selectedStatus !== 'Todos' && property.status !== selectedStatus)
        return false;
      if (selectedBedrooms !== 'Todos') {
        const minBeds = parseInt(selectedBedrooms.replace('+', ''), 10);
        if ((property.beds || 0) < minBeds) return false;
      }
      if (property.status === 'Venda') {
        const range = priceRanges[selectedPriceRange];
        if (property.price < range.min || property.price > range.max)
          return false;
      }
      if (
        selectedNeighborhood !== 'Todos' &&
        property.neighborhood !== selectedNeighborhood
      )
        return false;
      return true;
    });
  }, [
    allProperties,
    searchQuery,
    selectedType,
    selectedStatus,
    selectedBedrooms,
    selectedPriceRange,
    selectedNeighborhood,
  ]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedType('Todos');
    setSelectedStatus('Todos');
    setSelectedBedrooms('Todos');
    setSelectedPriceRange(0);
    setSelectedNeighborhood('Todos');
  };

  const hasActiveFilters =
    searchQuery ||
    selectedType !== 'Todos' ||
    selectedStatus !== 'Todos' ||
    selectedBedrooms !== 'Todos' ||
    selectedPriceRange !== 0 ||
    selectedNeighborhood !== 'Todos';

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-background border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4 lg:px-8">
          <nav className="flex items-center justify-between h-20">
            <Link to={`${basePath}`} className="flex items-center gap-2">
              <span className="font-serif text-2xl font-semibold text-foreground tracking-wide">
                {brandName}
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to={`${basePath}`}>
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Voltar ao site</span>
                </Button>
              </Link>
              {waLink ? (
                <a href={waLink} target="_blank" rel="noreferrer">
                  <Button variant="gold" size="sm" className="gap-2">
                    <Phone className="w-4 h-4" />
                    <span className="hidden sm:inline">WhatsApp</span>
                  </Button>
                </a>
              ) : (
                <Button variant="gold" size="sm" className="gap-2" disabled>
                  <Phone className="w-4 h-4" />
                  <span className="hidden sm:inline">WhatsApp</span>
                </Button>
              )}
            </div>
          </nav>
        </div>
      </header>

      <section className="bg-primary py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h1 className="font-serif text-4xl lg:text-5xl font-semibold text-primary-foreground mb-4">
            Encontre seu Imóvel Ideal
          </h1>
          <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-8">
            Explore os imóveis disponíveis com atendimento direto do corretor.
          </p>
          <div className="max-w-4xl mx-auto bg-background rounded-2xl p-4 shadow-medium">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar por localização, tipo ou nome..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 bg-muted/50 border-0"
                />
              </div>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-full md:w-48 h-12 bg-muted/50 border-0">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border shadow-lg z-[100]">
                  {propertyTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-full md:w-40 h-12 bg-muted/50 border-0">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border shadow-lg z-[100]">
                  {statusTypes.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="goldOutline"
                size="lg"
                className="h-12 gap-2"
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filtros
              </Button>
            </div>

            {showFilters && (
              <div className="mt-4 pt-4 border-t border-border animate-fade-in">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Select
                    value={selectedBedrooms}
                    onValueChange={setSelectedBedrooms}
                  >
                    <SelectTrigger className="h-12 bg-muted/50 border-0">
                      <SelectValue placeholder="Quartos" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border border-border shadow-lg z-[100]">
                      {bedroomOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option === 'Todos' ? 'Quartos' : `${option} quartos`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={String(selectedPriceRange)}
                    onValueChange={(v) => setSelectedPriceRange(Number(v))}
                  >
                    <SelectTrigger className="h-12 bg-muted/50 border-0">
                      <SelectValue placeholder="Faixa de preço" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border border-border shadow-lg z-[100]">
                      {priceRanges.map((range, index) => (
                        <SelectItem key={index} value={String(index)}>
                          {range.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={selectedNeighborhood}
                    onValueChange={setSelectedNeighborhood}
                  >
                    <SelectTrigger className="h-12 bg-muted/50 border-0">
                      <SelectValue placeholder="Bairro" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border border-border shadow-lg z-[100]">
                      {neighborhoods.map((neighborhood) => (
                        <SelectItem key={neighborhood} value={neighborhood}>
                          {neighborhood}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="lg"
                      className="h-12 text-destructive hover:text-destructive gap-2"
                      onClick={clearFilters}
                    >
                      <X className="w-4 h-4" />
                      Limpar filtros
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground">
                {filteredProperties.length}
              </span>{' '}
              {filteredProperties.length === 1
                ? 'imóvel encontrado'
                : 'imóveis encontrados'}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">
                Visualizar:
              </span>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {filteredProperties.length > 0 ? (
            <div
              className={
                viewMode === 'grid'
                  ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6'
                  : 'flex flex-col gap-4'
              }
            >
              {filteredProperties.map((property) => (
                <article
                  key={property.id}
                  className={`group bg-card rounded-2xl overflow-hidden border border-border hover:border-gold/30 transition-all duration-300 shadow-card hover:shadow-medium ${
                    viewMode === 'list' ? 'flex flex-col md:flex-row' : ''
                  }`}
                >
                  <div
                    className={`relative overflow-hidden ${viewMode === 'list' ? 'md:w-80 aspect-[4/3] md:aspect-auto' : 'aspect-[4/3]'}`}
                  >
                    {property.image ? (
                      <img
                        src={property.image}
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="text-muted-foreground text-sm">
                          Sem imagem
                        </span>
                      </div>
                    )}
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                        {property.type}
                      </span>
                      <span
                        className={`px-3 py-1 text-xs font-medium rounded-full ${property.status === 'Venda' ? 'bg-gold text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}
                      >
                        {property.status}
                      </span>
                      {property.featured && (
                        <span className="px-3 py-1 bg-foreground text-background text-xs font-medium rounded-full">
                          Destaque
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    className={`p-6 space-y-4 ${viewMode === 'list' ? 'flex-1' : ''}`}
                  >
                    <div>
                      <p className="font-serif text-2xl font-semibold text-gold">
                        {property.priceFormatted}
                      </p>
                      <h3 className="font-serif text-xl font-semibold text-foreground mt-1">
                        {property.title}
                      </h3>
                      <div className="flex items-center gap-1 text-muted-foreground text-sm mt-2">
                        <MapPin className="w-4 h-4" />
                        {property.location}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 pt-4 border-t border-border">
                      <div className="flex items-center gap-1 text-muted-foreground text-sm">
                        <Bed className="w-4 h-4" />
                        <span>
                          {property.beds}{' '}
                          {property.beds === 1 ? 'quarto' : 'quartos'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground text-sm">
                        <Bath className="w-4 h-4" />
                        <span>
                          {property.baths}{' '}
                          {property.baths === 1 ? 'banheiro' : 'banheiros'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground text-sm">
                        <Square className="w-4 h-4" />
                        <span>
                          {property.area ? `${property.area}m²` : '—'}
                        </span>
                      </div>
                    </div>
                    <Link to={`${basePath}/imovel/${property.id}`}>
                      <Button
                        variant={viewMode === 'list' ? 'goldOutline' : 'ghost'}
                        size="sm"
                        className={viewMode === 'list' ? 'mt-2' : 'w-full'}
                      >
                        Ver detalhes
                      </Button>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-foreground mb-2">
                Nenhum imóvel encontrado
              </h3>
              <p className="text-muted-foreground mb-6">
                Tente ajustar os filtros para encontrar mais opções
              </p>
              <Button variant="gold" onClick={clearFilters}>
                Limpar filtros
              </Button>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-primary-foreground mb-4">
            Não encontrou o que procura?
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
            Fale comigo e me diga o que você está buscando. Posso te ajudar com
            opções que ainda não estão publicadas aqui.
          </p>
          {waLink ? (
            <a href={waLink} target="_blank" rel="noreferrer">
              <Button variant="gold" size="lg" className="gap-2">
                <Phone className="w-5 h-5" />
                Falar no WhatsApp
              </Button>
            </a>
          ) : (
            <Button variant="gold" size="lg" className="gap-2" disabled>
              <Phone className="w-5 h-5" />
              WhatsApp indisponível
            </Button>
          )}
        </div>
      </section>

      <footer className="bg-foreground py-8">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <p className="text-muted/80 text-sm">
            © {new Date().getFullYear()} {brandName}. Todos os direitos
            reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Properties;
